import React from 'react';

import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ProviderOrder } from '../../../../models/provider.order.model';
import { ProviderInvoice } from '../../../../models/provider.invoice.model';
import { Expense } from '../../../../models/expense.model';
import { dateFormat, datetimeFormat12h } from '../../../../scripts/date.time.format.script';
import { countryName } from '../../../../libraries/country.library';
import { contactTypeValue } from '../../../../libraries/contact.type.library';
import { formatCurrency, formatNumberAddress, normalizeContactData } from '../../../../scripts/format.value.script';

export interface AppPurchaseViewDetailProps {
  purchase: string,
  record: any | ProviderOrder | ProviderInvoice | Expense,
  employee?: any
};

const AppPurchaseViewDetail: React.FunctionComponent<AppPurchaseViewDetailProps> = ({purchase, record, employee}) => {
  const {lang} = StateLanguage()

  if (record?.provider) normalizeContactData(record.provider)

  return (
    <div className="card-body">
      <div className="d-flex flex-wrap">
        <div className="flex-equal me-5">
          <table className="table align-middle gy-1 m-0">
            <tbody className="fw-bold">
              { purchase === "invoice" &&
                <tr>
                  <td className="w-200px text-muted">{lang.labels.orderNumber}:</td>
                  <td className="min-w-200px">{record.provider_order?.id?.length > 0 ? record.provider_order.provider_order_number : lang.labels.none}</td>
                </tr>
              }
              { purchase !== "expense" &&
                <tr>
                  <td className="w-200px text-muted">{lang.labels[`${purchase}Reference` as keyof typeof lang.labels]}:</td>
                  <td className="min-w-200px">{(record.reference ?? '').length > 0 ? record.reference : lang.labels.none}</td>
                </tr>
              }
              <tr>
                <td className="w-200px text-muted">{lang.labels.concerning}:</td>
                <td className="min-w-200px">{record.concerning}</td>
              </tr>
              { employee
                ?
                <tr>
                  <td valign="top" className="w-200px text-muted">{lang.labels.employee}:</td>
                  <td className="min-w-200px">
                    { (() => {
                      const d = employee.data ?? employee
                      const name = employee.type === 'person'
                        ? `${d.first_name ?? ''} ${d.last_name ?? ''}`.trim() || d.name || '—'
                        : d.name || `${d.first_name ?? ''} ${d.last_name ?? ''}`.trim() || '—'
                      const addr = d.address
                      return (
                        <>
                          <div>{name}</div>
                          { addr &&
                            <>
                              <p className="text-secondary fs-8 p-0 m-0">{addr.street ?? ''} {formatNumberAddress(lang, addr.number ?? '')}</p>
                              <p className="text-secondary fs-8 p-0 m-0">{addr.city ?? ''}{(addr.zip_code ?? '').length > 0 && ` - ${addr.zip_code}`}</p>
                              <p className="text-secondary fs-8 p-0 m-0">{countryName(lang.code, addr.country ?? '')}</p>
                            </>
                          }
                        </>
                      )
                    })()}
                  </td>
                </tr>
                :
                <>
                  <tr>
                    <td className="w-200px text-muted">{lang.labels.company}:</td>
                    <td className="min-w-200px">{record.company?.name ?? ''}</td>
                  </tr>
                  <tr>
                    <td valign="top" className="w-200px text-muted">{lang.labels.provider}:</td>
                    <td className="min-w-200px">
                      { record.provider
                        ?
                        <>
                          <div className="d-flex align-items-center">
                            {record.provider.type === "person"
                              ? `${record.provider.data?.first_name ?? ''} ${record.provider.data?.last_name ?? ''}`.trim() || record.provider.data?.name || '—'
                              : record.provider.data?.name || `${record.provider.data?.first_name ?? ''} ${record.provider.data?.last_name ?? ''}`.trim() || record.provider.name || '—'}
                            { record.provider.data?.id &&
                              <span className="badge badge-sm badge-light text-uppercase fs-9 ms-3">{contactTypeValue(lang, record.provider.type ?? '')}</span>
                            }
                          </div>
                          {(record.provider.data?.contact_code ?? '').length > 0 &&
                            <p className="text-secondary fs-8 p-0 m-0">{lang.labels.code}: {record.provider.data.contact_code}</p>
                          }
                          { record.provider.data?.address &&
                            <>
                              <p className="text-secondary fs-8 p-0 m-0">{record.provider.data?.address?.street ?? ''} {formatNumberAddress(lang, record.provider.data?.address?.number ?? '')}</p>
                              <p className="text-secondary fs-8 p-0 m-0">{record.provider.data?.address?.city ?? ''}{(record.provider.data?.address?.zip_code ?? '').length > 0 && ` - ${record.provider.data.address.zip_code}`}</p>
                              <p className="text-secondary fs-8 p-0 m-0">{countryName(lang.code, record.provider.data?.address?.country ?? '')}</p>
                            </>
                          }
                        </>
                        :
                        <span>—</span>
                      }
                    </td>
                  </tr>
                </>
              }
            </tbody>
          </table>
        </div>
        <div className="flex-equal">
          <table className="table align-middle gy-1 m-0">
            <tbody className="fw-bold">
              <tr>
                <td className="w-200px text-muted">{lang.labels[`${purchase}Date` as keyof typeof lang.labels]}:</td>
                <td className="min-w-200px">{dateFormat(lang.code, record.created_date)}</td>
              </tr>
              <tr>
                <td className="w-200px text-muted">{lang.labels.createdAt}:</td>
                <td className="min-w-200px">{datetimeFormat12h(lang.code, record.created_at)}</td>
              </tr>
              { record.valid_until &&
                <tr>
                  <td className="w-200px text-muted">{lang.labels.validUntil}:</td>
                  <td className="min-w-200px">{dateFormat(lang.code, record.valid_until)}</td>
                </tr>
              }
              { record.acceptance_status && record.acceptance_status !== 'not_required' &&
                <tr>
                  <td className="w-200px text-muted">{lang.labels.acceptanceStatus}:</td>
                  <td className="min-w-200px">
                    <span className={`badge badge-light-${record.acceptance_status === 'accepted' ? 'success' : record.acceptance_status === 'declined' ? 'danger' : 'warning'} text-uppercase fs-8`}>
                      {record.acceptance_status === 'accepted' ? lang.labels.accepted : record.acceptance_status === 'declined' ? lang.labels.declined : lang.labels.acceptancePending}
                    </span>
                  </td>
                </tr>
              }
              <tr>
                <td className="w-200px text-muted">{lang.labels.currency}:</td>
                <td className="min-w-200px">{formatCurrency(record.foreign_currency ?? record.currency)}</td>
              </tr>
              { purchase === "expense" &&
                <tr>
                  <td className="w-200px text-muted">{lang.labels.myTreasuryAccount}:</td>
                  <td className="min-w-200px">{record.treasury_account?.name ?? ''}</td>
                </tr>
              }
              { purchase !== "expense" &&
                <tr>
                  <td className="w-200px text-muted">{lang.labels.providerBankAccount}:</td>
                  <td className="min-w-200px">{record.provider_treasury_account?.name ?? ''}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
};

export default AppPurchaseViewDetail;
